import React, { useState } from "react";
import {
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Box,
  Container,
  Grid,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  useMediaQuery,
  useTheme,
  Divider,
  styled,
  Stack,

} from "@mui/material";
import { buttonColor, buttonHoverColor, lightgreenColor, primaryColorDark, primaryColorPurple } from "../../Util/UtilColorPalete";
import logoImage from "../../assets/images/logo.png";
import { financeData, itServicesData, financeSteps, Fonts, servicesSteps, medicalSteps, healthcareData, bankData, bankSteps, logisticSteps, logisticData } from "../../Util/UtilData";
import { Link, useParams } from "react-router-dom";
import FinanceImage from "../../assets/images/finance.jpg";
import BankImage from "../../assets/images/bank.jpg";
import HealthCareImage from "../../assets/images/healthcare.jpg";
import ItImage from "../../assets/images/it.jpg";
import LogisticsImage from "../../assets/images/logistics.jpg";
import { motion } from 'framer-motion';

// const countries = ["Australia", "United States", "Canada", "United Kingdom", "Germany", "France"];
const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 8,
    "&.Mui-focused fieldset": {
      borderColor: primaryColorPurple,
    },
  },
  "& .MuiInputLabel-root": {
    "&.Mui-focused": {
      color: primaryColorPurple,
    },
  },
}));

// Define animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3, // Stagger child animations by 0.3s
    },
  },
};

const textVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } },
};
// Validation rules
const validateField = (field, value) => {
  if (field.required && !value) return `${field.label} is required`;
  if (field.type === "text" && field.label.includes("Email") && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "Invalid email format";
  if (field.type === "text" && field.label.includes("Phone") && value && !/^\+?[1-9]\d{1,14}$/.test(value)) return "Invalid phone number";
  // if (field.type === "text" && field.label.includes("Url") && value && !/^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w- ./?%&=]*)?$/.test(value)) return "Invalid URL";
  return "";
};

const CRMForm = () => {
  const theme = useTheme();
  const params = useParams();
  const { name } = params;
  const formConfig = {
    Finance: { values: financeData, steps: financeSteps, template: FinanceImage },
    IT: { values: itServicesData, steps: servicesSteps, template: ItImage },
    Bank: { values: bankData, steps: bankSteps, template: BankImage },
    Healthcare: { values: healthcareData, steps: medicalSteps, template: HealthCareImage },
    Logistic: { values: logisticData, steps: logisticSteps, template: LogisticsImage }
  };

  // Using object destructuring with a default fallback
  const { values: initialFormValues, steps, template } = formConfig[name] || formConfig.Logistic
  const [formValues, setFormValues] = useState(initialFormValues);
  const [errors, setErrors] = useState({});
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const [uploadedImages, setUploadedImages] = useState({
    homePage: {
      BannerBackgroundImage: null,
      companylogo: null
    },
    aboutPage: {
      aboutImage: null
    },
    teamPage: {
      teamImage: null
    }
  });
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const clearForm = () => {
    // Reset form values to initial state
    setFormValues(initialFormValues);
    // Clear uploaded images
    setUploadedImages({
      homePage: {
        BannerBackgroundImage: null,
        companylogo: null
      },
      aboutPage: {
        aboutImage: null
      },
      teamPage: {
        teamImage: null
      }
    });
    // Clear errors
    setErrors({});
    // Reset step to beginning
    setActiveStep(0);
  };
  const handleChange = (page, field, value, index = null, subKey = null, subField = null) => {
    setFormValues((prev) => {
      const updatedForm = JSON.parse(JSON.stringify(prev));
      if (subField) {
        updatedForm.pages[page][field][subField].value = value;
      } else if (index !== null && subKey !== null) {
        updatedForm.pages[page][field].value[index][subKey].value = value;
      } else if (index !== null) {
        updatedForm.pages[page][field].value[index] = value;
      } else {
        updatedForm.pages[page][field].value = value;
      }
      return updatedForm;
    });

    const currentPageData = formValues.pages[page];
    let error = "";
    if (subField) {
      error = validateField(currentPageData[field][subField], value);
      setErrors((prev) => ({ ...prev, [`${page}.${field}.${subField}`]: error }));
    } else if (index !== null && subKey !== null) {
      error = validateField(currentPageData[field].value[index][subKey], value);
      setErrors((prev) => ({ ...prev, [`${page}.${field}.${index}.${subKey}`]: error }));
    } else {
      error = validateField(currentPageData[field], value);
      setErrors((prev) => ({ ...prev, [`${page}.${field}`]: error }));
    }
  };

  const handleImageChange = (page, field, file) => {
    setUploadedImages((prev) => ({
      ...prev,
      [page]: {
        ...prev[page],
        [field]: file
      }
    }));

    if (file) {
      setErrors((prev) => ({ ...prev, [`${page}.${field}`]: "" }));
    }
  };

  const validatePage = (pageData, pageName) => {
    const newErrors = {};
    Object.keys(pageData).forEach((key) => {
      const field = pageData[key];
      if (field.type === "text" || field.type === "textarea" || field.type === "select" || field.type === "number") {
        newErrors[`${pageName}.${key}`] = validateField(field, field.value);
      } else if (field.type === "image" && field.required && !uploadedImages[pageName]?.[key]) {
        newErrors[`${pageName}.${key}`] = `${field.label} is required`;
      } else if (field.type === "array") {
        field.value.forEach((item, index) => {
          Object.keys(item).forEach((subKey) => {
            newErrors[`${pageName}.${key}.${index}.${subKey}`] = validateField(item[subKey], item[subKey].value);
          });
        });
      }
    });
    setErrors((prev) => ({ ...prev, ...newErrors }));
    return Object.values(newErrors).every((error) => !error);
  };

  const ImageUploadField = ({ label, value, sizelabel, onChange, format, error, required, fileName }) => (
    <>
      <Typography variant="body1" align="left" gutterBottom color="gray">
        {label} {required && <span style={{ color: "red" }}>*</span>}
      </Typography>
      <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", mt: 2 }}>
        <Button
          variant="contained"
          component="label"
          sx={{ bgcolor: primaryColorPurple, color: "white", "&:hover": { bgcolor: buttonHoverColor, fontWeight: 'bold' } }}
        >
          Choose File
          <input type="file" accept={format} hidden onChange={(e) => onChange(e.target.files[0])} />
        </Button>
        {value ? (
          <Typography variant="body2" sx={{ color: "gray", ml: 2 }}>
            {value.name}
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ color: "gray", ml: 2 }}>
            No file chosen
          </Typography>
        )}
      </Box>
      <Typography variant="caption" display="block" gutterBottom sx={{ color: "gray", textAlign: "left", mt: 1 }}>
        {`Upload ${format === ".zip" ? "zip file" : "Image"} with image format png or jpeg or jpg `} {sizelabel && `(Image must be ${sizelabel})`}
      </Typography>
      {error && <Typography color="error" textAlign={"left"} fontSize={"12px"}>{error}</Typography>}
    </>
  );

  const renderFormFields = (pageData, pageName) => {


    return Object.keys(pageData).map((key) => {
      const field = pageData[key];
      if (field.type === "text" || field.type === "textarea" || field.type === "number") {
        return (
          <StyledTextField
            key={key}
            label={field.label}
            required={field.required}
            variant="outlined"
            fullWidth
            type={field.type === "number" ? "number" : "text"}
            multiline={field.type === "textarea"}
            placeholder={`enter the ${field.label}`}
            rows={field.type === "textarea" ? 4 : 0}
            value={field.value}
            onChange={(e) => handleChange(pageName, key, e.target.value)}
            error={!!errors[`${pageName}.${key}`]}
            helperText={errors[`${pageName}.${key}`]}
            sx={{ mb: 2, mt: 1, "& .MuiOutlinedInput-root": { backgroundColor: "#F5F5F5" } }}
          />
        );
      }
      else if (field.type === "color") {
        return (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="body1"
              color={primaryColorDark}
              sx={{ mb: 1, fontWeight: "bold" }}
            >
              {field.label}<span style={{ color: "red" }}>*</span>
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: "row", gap: 2 }}>
              <StyledTextField
                type="color"
                value={field.value || field.defaultColor} // Default to black if no value
                fullWidth
                onChange={(e) => handleChange(pageName, key, e.target.value)}
                variant="outlined"
                sx={{ width: 80, }}
                error={!!errors[`${pageName}.${key}`]}
                helperText={errors[`${pageName}.${key}`]}
                required
                InputProps={{
                  sx: { padding: 0 }
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: field.value || field.defaultColor,
                  // minWidth: 100, // Ensures consistent spacing
                  fontSize: "16px"

                }}
              >
                {field.value ? field.value : field.defaultColor}
              </Typography>
            </Box>

          </Box>
        )
      }
      else if (field.type === "dropdown") {
        return (
          <FormControl
            style={{
              // marginBottom: "20px",
              width: "50%",
              fontSize: "16px",
            }}

          >
            <InputLabel
              // id="demo-simple-select-label"
              style={{ color: primaryColorDark }}

              InputProps={{
                style: { height: "30px", color: primaryColorDark },
              }}
              InputLabelProps={{ shrink: true, style: { color: primaryColorDark } }}
            >
              Font
            </InputLabel>

            <Select
              style={{ padding: "0px", color: primaryColorDark }}
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={field.value || field.defaultValue}
              label="Fonts"
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: primaryColorDark, // Default border color
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: primaryColorDark, // Border color on hover
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: primaryColorDark, // Border color when focused
                },
              }}
              onChange={(e) => handleChange(pageName, key, e.target.value)}
            // MenuProps={MenuProps}
            >
              {Fonts.map((font) => (
                <MenuItem
                  // style={{ fontFamily: fontFamilyall }}
                  value={font}
                >
                  {font}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )
      }
      else if (field.type === "image") {
        return (
          <Box key={key}>
            <ImageUploadField
              label={`Upload ${field.label}`}
              value={uploadedImages[pageName]?.[key]}
              sizelabel={field.size}
              fileName={pageName}
              required={field.required}
              onChange={(file) => handleImageChange(pageName, key, file)}
              format={field.format}
              error={errors[`${pageName}.${key}`]}
            />
          </Box>
        );
      } else if (field.type === "array" && field.align === "column") {
        return field.value.map((item, index) => (
          <Box key={index} sx={{ mb: 2 }}>
            <Grid container spacing={2}>
              {Object.keys(item).map((subKey) => {
                return (
                  <Grid item xs={12} md={12} key={`${key}-${index}-${subKey}`}>
                    <StyledTextField
                      required={item[subKey]?.required}
                      label={

                        item[subKey]?.label
                      }
                      variant="outlined"
                      placeholder={item[subKey]?.label}
                      disabled={item[subKey]?.disabled}
                      fullWidth
                      multiline={item[subKey].type === "textarea"}
                      rows={item[subKey].type === "textarea" ? 4 : 0}
                      value={item[subKey].value}
                      onChange={(e) => handleChange(pageName, key, e.target.value, index, subKey)}
                      error={!!errors[`${pageName}.${key}.${index}.${subKey}`]}
                      helperText={errors[`${pageName}.${key}.${index}.${subKey}`]}
                      sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#F5F5F5" } }}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        ));
      }
      else if (field.type === "array" && field.align === "row") {
        return field.value.map((item, index) => (
          <Box key={index} sx={{ mb: 2 }}>
            <Grid container spacing={2}>
              {Object.keys(item).map((subKey) => {
                const condition = 6;
                return (
                  <Grid item xs={12} md={condition} key={`${key}-${index}-${subKey}`}>
                    <StyledTextField
                      required={item[subKey]?.required}
                      label={

                        item[subKey]?.label
                      }
                      variant="outlined"
                      placeholder={
                        item[subKey]?.label}
                      fullWidth
                      disabled={item[subKey]?.disabled === true}
                      multiline={item[subKey].type === "textarea"}
                      rows={item[subKey].type === "textarea" ? 4 : 0}
                      value={item[subKey].value}
                      onChange={(e) => handleChange(pageName, key, e.target.value, index, subKey)}
                      error={!!errors[`${pageName}.${key}.${index}.${subKey}`]}
                      helperText={errors[`${pageName}.${key}.${index}.${subKey}`]}
                      sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#F5F5F5" } }}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        ));
      }
      return null;
    });
  };

  const handleSubmit = async () => {
    const currentPage = Object.keys(formValues.pages)[activeStep];
    const isValid = validatePage(formValues.pages[currentPage], currentPage);

    if (isValid && activeStep === steps.length - 1) {
      setIsSubmitting(true);
      try {
        await new Promise(resolve => setTimeout(resolve, 2000)); // Simulated 2-second delay
        setIsSubmitting(false);
        setIsSubmitted(true);
        setActiveStep(0);
        clearForm();
        // setFormValues({})
      } catch (error) {
        setIsSubmitting(false);
        // Handle error if needed
      }
    } else if (isValid) {
      setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
    }
  };

  // const handleNext = () => {
  //   const currentPage = Object.keys(formValues.pages)[activeStep];
  //   const isValid = validatePage(formValues.pages[currentPage], currentPage);
  //   if (isValid) {
  //     setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
  //   }
  // };

  const handlePrevious = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };
  const ThankYouSection = () => {
    // Variants for the body text letter animation
    const textVariants = {
      hidden: { opacity: 0 },
      visible: {
        opacity: 1,
        transition: {
          staggerChildren: 0.05, // Stagger each letter
        },
      },
    };

    const letterVariants = {
      hidden: { opacity: 0, y: 20 },
      visible: { opacity: 1, y: 0 },
    };

    const bodyText = "Sit back and enjoy the process—we’re busy creating something amazing for you!We’ll reach out to you soon!";

    return (
      <Container
        maxWidth="xxl"
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Stack
          direction="column"
          spacing={4}
          sx={{
            flexGrow: 1,
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          }}
        >
          {/* Image Container with Spring Bounce */}


          {/* Success Message with Slide-Up Spring */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 80, damping: 15, delay: 0.2 }}
          >
            <Typography
              variant="h4"
              // component="h1"
              sx={{
                px: { xs: 2, sm: 3 },
                py: 0.5,
                borderRadius: 1,
                // fontWeight: 'medium',
                // fontSize: { xs: '0.9rem', sm: '1.25rem' },
                // maxWidth: { xs: '90%', sm: '90%' },
                color: lightgreenColor,
              }}
            >
              Thank you for trusting us!
            </Typography>
          </motion.div>

          {/* Body Text with Staggered Letter Animation */}
          <motion.div
            variants={textVariants}
            initial="hidden"
            animate="visible"
            style={{ overflow: 'hidden' }}
          >
            <Typography variant="h6" color="textPrimary">
              {bodyText.split('').map((char, index) => (
                <motion.span key={index} variants={letterVariants}>
                  {char}
                </motion.span>
              ))}
            </Typography>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: 'spring', stiffness: 100, damping: 10 }}
            style={{
              width: '100%',
              maxWidth: { xs: '90%', sm: '50%' },
              height: 'auto',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              overflowY: 'auto',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            <Link to="/">
              <Box
                component="img"
                src={template}
                alt="Services Illustration"
                sx={{
                  width: { xs: '100%', sm: 600 },
                  height: { xs: 'auto', sm: 350 },
                  maxWidth: '100%',
                  borderRadius: 2,
                  objectFit: 'contain',
                  cursor: 'pointer',
                  transition: 'transform 0.3s ease-in-out',
                  '&:hover': { transform: 'scale(1.02)' },
                }}
              />
            </Link>
          </motion.div>
        </Stack>

        {/* Divider with Scale Animation */}
        <Box sx={{ bottom: 0, position: 'relative' }}>
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.8, ease: 'easeInOut', delay: 0.5 }}
          >
            <Divider sx={{ borderColor: 'lightgray', my: 2 }} />
          </motion.div>

          {/* Footer with Fade-In */}
          {/* <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1 }}
          > */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="gray">
              {"Privacy Policy | Terms & Conditions | © 2025 Company Name. All rights reserved."}
            </Typography>
          </Box>
          {/* </motion.div> */}
        </Box>
      </Container>
    );
  };
  return (
    <>
      <Box
        sx={{
          position: isSubmitted ? 'static' : 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          backgroundColor: 'white',
          px: isMobile ? 1 : isTablet ? 3 : 3,
          py: 4,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'center',
            // py: 2,
          }}
        >
          <Box
            sx={{
              width: isMobile ? '100%' : '10%',
              display: 'flex',
              justifyContent: isMobile ? 'center' : 'flex-start',
              // mb: isMobile ? 2 : 0,
            }}
          >
            <Link to="/">
              <img
                src={logoImage}
                style={{
                  width: isMobile ? '40%' : '80%',
                  height: 'auto',
                  maxWidth: '100px',
                }}
                alt="Logo"
              />
            </Link>

          </Box>
          <Box
            sx={{
              width: isMobile ? '100%' : '110%',
              textAlign: 'center',
            }}
          >
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <motion.div variants={textVariants}>
                <Typography
                  variant={isMobile ? 'h5' : 'h6'}
                  sx={{
                    fontWeight: '900',
                    color: primaryColorPurple,
                    fontFamily: 'Nunito Sans',
                    fontSize: isMobile ? '1.5rem' : isTablet ? '2rem' : '2rem',
                  }}
                >
                  BigFive CRM Templates
                </Typography>
              </motion.div>
              {
                !isSubmitted &&
                <motion.div variants={textVariants}>
                  <Typography
                    variant={isMobile ? 'h5' : 'h2'}
                    sx={{
                      color: 'gray',
                      fontFamily: 'Nunito Sans',
                      fontSize: isMobile ? '1.5rem' : isTablet ? '2rem' : '2rem',
                    }}
                    mt={2}
                  >
                    Just a few clicks stand between you and your brand-new website!
                  </Typography>
                </motion.div>
              }

            </motion.div>
          </Box>
        </Box>
        {
          !isSubmitted &&
          (
            <>
              <Box
                sx={{
                  display: 'flex',
                  width: '100%',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Box sx={{ width: '100%', maxWidth: '900px', display: 'flex', justifyContent: 'center' }} ml={isMobile?0:12}>
                  <Stepper
                    activeStep={activeStep}
                    alternativeLabel
                    sx={{
                      flexGrow: 1, // Ensures it expands
                      pb: 2,
                      marginTop: '10px',
                      width: '100%', // Ensures it takes full width
                      padding: '20px 0',

                      '& .MuiStepConnector-root': {
                        top: '12px',
                        '& .MuiStepConnector-line': {
                          borderColor: '#bdbdbd',
                          borderWidth: '2px',
                          transition: 'border-color 0.3s ease-in-out',
                        },
                      },
                      '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line': {
                        borderColor: primaryColorPurple,
                      },
                      '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': {
                        borderColor: '#388e3c',
                      },

                      '& .MuiStepLabel-label': {
                        fontSize: isMobile ? '0.8rem' : '0.9rem',
                        fontWeight: 500,
                        color: '#424242',
                        transition: 'color 0.3s ease-in-out',
                      },
                      '& .MuiStepLabel-label.Mui-active': {
                        color: primaryColorPurple,
                        fontWeight: 600,
                      },
                      '& .MuiStepLabel-label.Mui-completed': {
                        color: '#388e3c',
                        fontWeight: 500,
                      },
                      '& .MuiStepLabel-iconContainer .MuiSvgIcon-root': {
                        fontSize: '1.5rem',
                        color: '#757575',
                        transition: 'color 0.3s ease-in-out',
                      },
                      '& .MuiStepLabel-iconContainer .MuiSvgIcon-root.Mui-active': {
                        color: primaryColorPurple,
                      },
                      '& .MuiStepLabel-iconContainer .MuiSvgIcon-root.Mui-completed': {
                        color: '#388e3c',
                      },

                      '@media (max-width: 600px)': {
                        padding: '15px 0',
                        '& .MuiStepLabel-label': {
                          fontSize: '0.75rem',
                        },
                        '& .MuiSvgIcon-root': {
                          fontSize: '1.2rem',
                        },
                      },
                    }}
                  >
                    {steps.map((label, index) => (
                      <Step key={index}>
                        <StepLabel>{label}</StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </Box>
              </Box>

            </>
          )


        }

      </Box>
      {
        isSubmitted ? (
          <>
            {ThankYouSection()}

          </>
        ) : (
          <>
            <Container
              maxWidth="lg"
              sx={{
                pt: isMobile ? 40 : 35,
                pb: 10,
                minHeight: '100vh',
              }}
            >

              <Box sx={{ justifyContent: 'center', alignItems: 'center' }}>
                {renderFormFields(Object.values(formValues.pages)[activeStep], Object.keys(formValues.pages)[activeStep])}
              </Box>

              <Box
                sx={{
                  position: 'fixed',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  bgcolor: 'white',
                  py: 2,
                  px: 4,
                  display: 'flex',
                  justifyContent: 'space-between',
                  zIndex: 1000,
                }}
              >
                <Button
                  variant="contained"
                  onClick={handlePrevious}
                  disabled={activeStep === 0 || isSubmitting}
                  sx={{ minWidth: '100px', backgroundColor: primaryColorPurple, fontWeight: 'bold' }}
                >
                  Previous
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  sx={{ minWidth: '100px', backgroundColor: primaryColorPurple, fontWeight: 'bold' }}
                >
                  {activeStep === steps.length - 1 ? 'Create Template' : 'Next'}
                </Button>
              </Box>

            </Container>

          </>
        )
      }

    </>
  );
};

export default CRMForm;